/**
 * AstroVastuScreen — the L1A "Astro-Vastu" stage tab (confirm view + WRITE).
 *
 * Built to the owner-approved mockup v2
 * (Deliverables/2026-06-30-astro-vastu-tab-mockup-v2.html). It surfaces the
 * already-live astro engine (resolveAstroFamily → astroBedroom → astroAllocation)
 * for one project: the bedroom zones MARKED during Per-property are auto-fetched
 * and each is checked against every family member's chart.
 *
 * Layers (mockup order):
 *   1. Source banner   — the marked bedrooms auto-fetched from Per-property.
 *   2. Breadwinner headline — the breadwinner leads the zoning decision.
 *   3. Family roster   — per member: ascendant → ideal zone → achievable-here.
 *   4. Room allocation — who sleeps where (each marked bedroom → a member).
 *   5. Entrance × chart — the chart-ideal entrance (or its pending placeholder).
 *   6. Confirm card    — the REAL "Confirm astro inputs" tick (owner ruling
 *      2026-07-03: nothing to override — confirming IS the tick). It snapshots
 *      exactly what this screen renders into `astro_vastu_confirmation` on the
 *      project doc (lib/astroVastuConfirm), which flips the stage chip to done
 *      and feeds the report's §E/§F. Confirmed state shows the banner + a quiet
 *      Re-confirm that overwrites with a fresh snapshot. No Save-draft — the
 *      owner's need is the tick.
 *
 * NON-BLOCKING by contract: a pending chart degrades to a clear "chart pending"
 * row, a null entranceVerdict renders the pending placeholder, and a failed /
 * not-yet-deployed callable shows a calm empty state — none of these crash.
 * ONE page scroll only — no inner scroll boxes (Rohit's hard UI rule).
 *
 * NO dead controls (owner hard rule 2026-07-03): every control rendered here
 * works. The per-row allocation Override stays removed — it returns only if a
 * real need for it ever appears.
 */
import { useEffect, useState, Fragment } from 'react';
import { Btn3, Card } from '../ui';
import {
  resolveAstroVastuForProject,
  type AstroVastuResolved,
  type AstroMember,
  type AstroAllocationRow,
} from '../../lib/astroVastu';
import {
  allocationOccupants,
  allocationRoomLabel,
  altZonesOf,
  buildAstroVastuConfirmation,
  buildBoxScaffold,
  idealZoneOf,
  writeAstroVastuConfirmation,
  zoneName,
  type AstroVastuConfirmation,
} from '../../lib/astroVastuConfirm';

type MatchKind = 'good' | 'warn' | 'crit' | 'mute';

interface MemberMatch {
  kind: MatchKind;
  /** Short pill label. */
  label: string;
}

/**
 * Resolve a member's "matched to a marked bedroom?" verdict from the reconciled
 * achievable zone (the engine authority) + the marked bedrooms. Pure + total.
 */
function memberMatch(m: AstroMember, markedZones: string[]): MemberMatch {
  if (m.chartStatus === 'pending') {
    return { kind: 'mute', label: 'Chart pending' };
  }
  const marked = new Set(markedZones.map((z) => z.toUpperCase()));
  const ach = m.achievableZone;
  if (ach && ach.zone) {
    // Forge's severity vocabulary: ok → green · remedy → amber ·
    // critical → red · unspecified/other → neutral. A null achievableZone
    // (handled below) is the only "not available" state.
    const sev = (ach.severity || '').toLowerCase();
    if (sev === 'ok') {
      return { kind: 'good', label: `Ideal match · marked ${ach.zone}` };
    }
    if (sev === 'remedy') {
      return { kind: 'warn', label: `Achievable · needs remedy · marked ${ach.zone}` };
    }
    if (sev === 'critical') {
      return { kind: 'crit', label: `Critical compromise · marked ${ach.zone}` };
    }
    return { kind: 'mute', label: `Achievable · marked ${ach.zone}` }; // unspecified
  }
  // No achievable zone reconciled — is the ideal zone even a marked bedroom?
  const ideal = m.idealZones.find((z) => z.klass === 'ideal')?.zone;
  if (ideal && marked.has(ideal.toUpperCase())) {
    return { kind: 'warn', label: `Positive · marked ${ideal}` };
  }
  return { kind: 'crit', label: 'Not available' };
}

function ascendantLabel(m: AstroMember): { en: string; sa: string | null } {
  return { en: m.ascendantSign ?? '—', sa: m.ascendantSanskrit ?? null };
}

/** "3 Jul 2026, 14:32" from the confirmation's ISO stamp (render-only). */
function confirmedOn(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function AstroVastuScreen({
  projectId,
  refreshKey,
  confirmation,
  actor,
  mayCommit,
  onMsg,
  resolver = resolveAstroVastuForProject,
  write = writeAstroVastuConfirmation,
}: {
  projectId: string;
  /**
   * Bump this to force a fresh `resolveAstroVastuForProject` without remounting
   * — the "Linked Family" box increments it on link/unlink so this tab re-reads
   * the roster/ascendant/allocation against the newly-linked family.
   */
  refreshKey?: number;
  /**
   * Live `astro_vastu_confirmation` off the project doc (null = not yet
   * confirmed). ProjectDetail watches it (watchAstroVastuConfirmation) and
   * shares the SAME value with the stage-chip derivation — banner and tick can
   * never disagree.
   */
  confirmation: AstroVastuConfirmation | null;
  /** The signed-in staff actor — uid is the Firebase Auth uid (`confirmed_by`). */
  actor: { uid: string | null; name: string };
  /** Commit rights (assignee staff or admin) — gates Confirm / Re-confirm. */
  mayCommit: boolean;
  onMsg: (m: string) => void;
  /** Test/harness seam — defaults to the real callable seam. */
  resolver?: (projectId: string) => Promise<AstroVastuResolved | null>;
  /** Test/harness seam — defaults to the real Firestore write. */
  write?: typeof writeAstroVastuConfirmation;
}) {
  const [data, setData] = useState<AstroVastuResolved | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmBusy, setConfirmBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      const resolved = await resolver(projectId);
      if (!alive) return;
      setData(resolved);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
    // `resolver` is a stable seam (default import / harness stub) — deliberately
    // not a dependency so an inline harness lambda can't loop the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, refreshKey]);

  /**
   * The tick. Snapshots the CURRENTLY-RENDERED resolve into
   * `astro_vastu_confirmation` (overwrite on Re-confirm — always a fresh
   * snapshot). Failure toasts the real error and stays retryable; the
   * confirmed banner only ever reflects what the live watch reads back.
   */
  async function confirmAstroInputs() {
    if (confirmBusy || !data) return;
    if (!actor.uid) {
      onMsg('Confirm failed: no signed-in staff user — sign in and retry.');
      return;
    }
    setConfirmBusy(true);
    try {
      const built = await buildAstroVastuConfirmation(data, { uid: actor.uid, name: actor.name });
      await write(projectId, built, actor.name);
      onMsg('Astro inputs confirmed — the Astro-Vastu stage is ticked.');
    } catch (e) {
      onMsg(`Confirm failed: ${(e as Error).message}`);
    } finally {
      setConfirmBusy(false);
    }
  }

  // ── loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="astro-vastu">
        <div className="av-empty" role="status" aria-live="polite">
          Resolving the family charts against the marked bedrooms…
        </div>
      </div>
    );
  }

  // ── empty / not-yet-resolved (callable failed or not deployed) ─────────────
  if (!data) {
    return (
      <div className="astro-vastu">
        <div className="av-hint">
          <span className="av-mk" aria-hidden>✦</span>
          <p style={{ margin: 0 }}>
            Astro-Vastu reads the bedrooms you marked in <b>Evaluate</b> and checks each
            against the family's charts. The resolver isn't available yet for this project.
          </p>
        </div>
        <div className="av-empty">
          Astro inputs are not resolved yet. Mark the bedrooms in Evaluate and, once the
          astro resolver is live for this project, this view fills in automatically.
        </div>
        {confirmation && (
          /* Previously confirmed, but the resolver is unavailable right now —
             show the tick read-only. Re-confirm needs a fresh resolve to
             snapshot, so no control is rendered here (no dead buttons). */
          <div className="av-confirmbar">
            <div className="av-cb-txt">
              <span className="av-status-pill good" role="status">
                <span aria-hidden>✓ </span>
                Confirmed by {confirmation.confirmed_by_name} on{' '}
                {confirmedOn(confirmation.confirmed_at)}
              </span>
              <p>
                The confirmed snapshot is locked on the project. Re-confirming needs the
                resolver — it becomes available once this view loads again.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  const { markedBedroomZones, members, allocation } = data;
  const breadwinner = members.find((m) => m.isBreadwinner) ?? members[0] ?? null;
  const nameById = new Map(members.map((m) => [m.personId, m.name]));
  const resolvedMembers = members.filter((m) => m.chartStatus === 'resolved');
  const pendingMembers = members.filter((m) => m.chartStatus === 'pending');

  const bwIdeal = breadwinner ? idealZoneOf(breadwinner) : null;
  const bwMatch = breadwinner ? memberMatch(breadwinner, markedBedroomZones) : null;
  const bwAsc = breadwinner ? ascendantLabel(breadwinner) : null;

  // ── Comparative Box Matrix ────────────────────────────────────────────────
  // Rows = the fixed 12 boxes (engine order, Box 1 = Ascendant); columns = each
  // member split into Sign + Direction. We index each member's boxMatrix by box
  // number for O(1) per-cell lookup; a member with an empty boxMatrix (chart
  // pending) yields no hit and renders a muted "—" column, never a broken cell.
  const boxScaffold = buildBoxScaffold(members);
  const memberBoxMaps = members.map(
    (m) => new Map(m.boxMatrix.map((r) => [r.box, r] as const)),
  );

  // The breadwinner's chart-IDEAL entrance verdict (source:'chart-ideal') — not
  // yet reconciled against the home's marked entrance; labelled honestly below.
  const bwVerdict = breadwinner?.entranceVerdict ?? null;

  return (
    <div className="astro-vastu">
      {/* 1 · SOURCE banner — bedrooms auto-fetched from Per-property markings */}
      <div className="av-source">
        <span className="av-src-ic" aria-hidden>⤵</span>
        <div className="av-src-body">
          <p className="av-src-lead">Auto-fetched · data flow</p>
          <p>
            Bedroom zones are pulled from your <b>Evaluate markings</b>
            {markedBedroomZones.length > 0 ? (
              <>
                {' '}— <b>{markedBedroomZones.length} bedroom{markedBedroomZones.length === 1 ? '' : 's'} found: {markedBedroomZones.join(', ')}</b>
              </>
            ) : (
              <> — <b>no bedrooms marked yet</b></>
            )}{' '}
            — each checked against the family's charts below. Astro-Vastu does not invent
            zones; it reads the bedrooms you marked on the plan.
          </p>
          {markedBedroomZones.length > 0 && (
            <p className="av-found">
              <span className="av-lab">Marked bedrooms:</span>
              {markedBedroomZones.map((z) => (
                <span className="av-zone marked" key={z}>{z}</span>
              ))}
            </p>
          )}
        </div>
      </div>

      {/* explainer hint */}
      <div className="av-hint">
        <span className="av-mk" aria-hidden>✦</span>
        <p style={{ margin: 0 }}>
          This view reads each member's <b>ascendant</b> and matches their <b>ideal bedroom
          zone</b> against the bedrooms you marked in Evaluate. It is <b>breadwinner-led</b>
          {' '}— the breadwinner's match is the headline, with per-member detail below.
          Confirming locks these inputs into the report.
        </p>
      </div>

      {/* 2 · BREADWINNER-LED HEADLINE */}
      {breadwinner && (
        <div className="av-headline">
          <p className="av-eyebrow"><span aria-hidden>♔</span> Breadwinner — leads the zoning decision</p>
          <div className="av-hl-grid">
            <div className="av-hl-cell">
              <span className="av-lab">Member &amp; ascendant</span>
              <span className="av-who">{breadwinner.name}</span>
              <span className="av-sub">
                {bwAsc?.en}
                {bwAsc?.sa ? <> · <span style={{ color: 'var(--ink-soft)' }}>{bwAsc.sa} lagna</span></> : null}
                {breadwinner.relationToClient ? <> · {breadwinner.relationToClient}</> : null}
              </span>
            </div>
            <div className="av-hl-cell">
              <span className="av-lab">Ideal bedroom zone</span>
              <span className="av-big-zone">{bwIdeal ?? (breadwinner.chartStatus === 'pending' ? '—' : 'n/a')}</span>
              <span className="av-sub">{bwIdeal ? zoneName(bwIdeal) : 'Awaiting chart'}</span>
            </div>
            <div className="av-hl-cell status">
              <span className="av-lab">Matched to a marked bedroom?</span>
              {bwMatch && (
                <span className={`av-status-pill ${bwMatch.kind}`}>
                  <span aria-hidden>
                    {bwMatch.kind === 'good' ? '✓' : bwMatch.kind === 'crit' ? '✕' : bwMatch.kind === 'mute' ? '⏳' : '≈'}
                  </span>
                  {bwMatch.label}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 3 · FAMILY ROSTER */}
      <Card>
        <h2 style={{ fontSize: 14.5, fontWeight: 700, margin: '0 0 3px' }}>Family roster</h2>
        <p style={{ fontSize: 12, color: 'var(--ink-mute)', margin: '0 0 14px' }}>
          Per member · ascendant → ideal zone, positive alternatives, matched against the
          bedrooms you marked in Evaluate{markedBedroomZones.length ? ` (${markedBedroomZones.join(', ')})` : ''}.
        </p>
        <div className="av-roster" role="table" aria-label="Family astro roster">
          <div className="av-rt-row head" role="row">
            <span className="av-cell" role="columnheader">Name</span>
            <span className="av-cell" role="columnheader">Relationship</span>
            <span className="av-cell" role="columnheader">Ascendant</span>
            <span className="av-cell" role="columnheader">Ideal zone</span>
            <span className="av-cell" role="columnheader">Positive alternatives</span>
            <span className="av-cell" role="columnheader">Matched to a marked bedroom?</span>
          </div>
          {members.map((m) => {
            const match = memberMatch(m, markedBedroomZones);
            const ideal = idealZoneOf(m);
            const alts = altZonesOf(m);
            const asc = ascendantLabel(m);
            return (
              <div className={`av-rt-row ${m.isBreadwinner ? 'bw' : ''}`} role="row" key={m.personId}>
                <span className="av-cell" role="cell" data-l="Name">
                  <span className="av-name">{m.name}</span>
                  {m.isBreadwinner && <span className="av-bw-mark"><span aria-hidden>♔</span> breadwinner</span>}
                </span>
                <span className="av-cell" role="cell" data-l="Relationship">{m.relationToClient || '—'}</span>
                <span className="av-cell" role="cell" data-l="Ascendant">
                  {m.chartStatus === 'pending' ? (
                    <span style={{ color: 'var(--ink-soft)' }}>chart pending</span>
                  ) : (
                    <>
                      <span className="av-sign-en">{asc.en}</span>
                      {asc.sa && <span className="av-sign-sa">/ {asc.sa}</span>}
                    </>
                  )}
                </span>
                <span className="av-cell" role="cell" data-l="Ideal zone">
                  {ideal ? <span className="av-zone lead">{ideal}</span> : <span style={{ color: 'var(--ink-soft)' }}>—</span>}
                </span>
                <span className="av-cell" role="cell" data-l="Positive alternatives">
                  {alts.length ? alts.map((z) => <span className="av-zone alt" key={z}>{z}</span>) : <span style={{ color: 'var(--ink-soft)' }}>—</span>}
                </span>
                <span className="av-cell" role="cell" data-l="Matched to a marked bedroom?">
                  <span className={`av-tag ${match.kind}`}>
                    <span aria-hidden>
                      {match.kind === 'good' ? '✓ ' : match.kind === 'crit' ? '✕ ' : match.kind === 'mute' ? '⏳ ' : '≈ '}
                    </span>
                    {match.label}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
        {pendingMembers.length > 0 && (
          <div className="av-pending-row">
            <span aria-hidden>⏳</span>
            <span>
              {pendingMembers.length} member{pendingMembers.length === 1 ? '' : 's'} with a
              chart still pending ({pendingMembers.map((m) => m.name).join(', ')}). Their zones
              fill in once the chart resolves — the roster degrades gracefully until then.
            </span>
          </div>
        )}
        {resolvedMembers.length === 0 && pendingMembers.length === 0 && (
          <div className="av-empty" style={{ marginTop: 10 }}>
            No family members resolved yet. Capture birth details at intake and link the astro
            family, then the roster populates here.
          </div>
        )}
      </Card>

      {/* 4 · ROOM ALLOCATION — who sleeps where */}
      <Card>
        <h2 style={{ fontSize: 14.5, fontWeight: 700, margin: '0 0 3px' }}>Room allocation — who sleeps where</h2>
        <p style={{ fontSize: 12, color: 'var(--ink-mute)', margin: '0 0 14px' }}>
          Each marked bedroom from Evaluate mapped to a member. The Acharya's allocation is
          what the report prints (§F).
        </p>
        {allocation.length > 0 ? (
          <div className="av-alloc">
            {allocation.map((row: AstroAllocationRow, i) => {
              // Forge returns a COMBINED row for a couple — the shared
              // allocationOccupants/allocationRoomLabel helpers render it AND
              // fill the confirm snapshot (one implementation, zero drift).
              const occupant = allocationOccupants(row, nameById);
              // `row.room` is the FLOOR label; the room identity is its zone.
              const roomTitle = allocationRoomLabel(row);
              const roomSub = [row.room, 'marked in Evaluate'].filter(Boolean).join(' · ');
              return (
                <div className={`av-alloc-row ${row.flagged ? 'flag' : ''}`} key={`${row.zone || 'z'}-${i}`}>
                  <div className="av-alloc-room">
                    <div className="av-room-badge">
                      <span className="av-z">{row.zone || '—'}</span>
                      <span className="av-t">bed</span>
                    </div>
                    <div>
                      <div className="av-rlbl">{roomTitle}</div>
                      <div className="av-rm-sub">{roomSub}</div>
                    </div>
                  </div>
                  <div className="av-alloc-mid">
                    <span className="av-occ">
                      {occupant}
                      {row.source === 'override' && <span className="av-tag mute">override</span>}
                      {row.flagged && <span className="av-tag warn">best available</span>}
                    </span>
                    {row.note && <span className="av-why">{row.note}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="av-empty">
            No allocation drafted yet. Once the marked bedrooms are reconciled against the
            family's ideal zones, the who-sleeps-where draft appears here.
          </div>
        )}
      </Card>

      {/* 5 · COMPARATIVE BOX MATRIX — 12 boxes (rows) × members (columns) */}
      <Card>
        <h2 style={{ fontSize: 14.5, fontWeight: 700, margin: '0 0 3px' }}>Comparative Box Matrix</h2>
        {/* layout-neutral subtitle — the table/stack captions below carry the
            breakpoint-specific "columns side-by-side" vs "card per member" copy */}
        <p style={{ fontSize: 12, color: 'var(--ink-mute)', margin: '0 0 10px' }}>
          The 12 life-context boxes (Box 1 = Ascendant), with the sign sitting on each box and
          its Vastu direction, per family member.
        </p>
        {boxScaffold.length > 0 ? (
          <>
            {/* Two renderings of the SAME matrix, CSS-swapped at the 860px shell
                breakpoint (each display:none'd copy leaves the a11y tree):
                ≥861px — the side-by-side table (unchanged); ≤860px — stacked
                per-member cards, page-flow only. The table's overflow-x wrapper
                horizontally scrolled INSIDE the page on phones (Vera F3 /
                owner's no-nested-scrollbars hard rule) — phones now stack. */}
            <p className="av-bm-cap av-bm-cap-table">
              Rows are the 12 Boxes (fixed); each family member is a column split into the Sign
              on that Box and its Vastu direction.
            </p>
            <p className="av-bm-cap av-bm-cap-stack">
              One card per family member — the 12 Boxes (Box 1 = Ascendant), each with the Sign
              sitting on it and its Vastu direction.
            </p>
            <div className="av-bm-scroll">
              <table className="av-bm">
                <thead>
                  <tr>
                    <th rowSpan={2} scope="col" className="av-bm-boxhead">Box · life-context</th>
                    {members.map((m) => (
                      <th key={m.personId} colSpan={2} scope="colgroup" className="av-bm-memhead">
                        {m.name}
                        {m.isBreadwinner && <span className="av-bm-crown" aria-hidden> ♔</span>}
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {members.map((m) => (
                      <Fragment key={m.personId}>
                        <th scope="col" className="av-bm-sub">Sign</th>
                        <th scope="col" className="av-bm-sub">Direction</th>
                      </Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {boxScaffold.map((row) => (
                    <tr key={row.box}>
                      <th scope="row" className="av-bm-box">
                        <span className="av-bm-num">{row.box}</span>
                        <span className="av-bm-ctx">{row.lifeContext || '—'}</span>
                      </th>
                      {members.map((m, mi) => {
                        const cell = memberBoxMaps[mi].get(row.box);
                        if (!cell) {
                          return (
                            <td
                              key={m.personId}
                              colSpan={2}
                              className="av-bm-mute"
                              title={m.chartStatus === 'pending' ? 'Chart pending' : 'No box data for this member'}
                            >
                              —
                            </td>
                          );
                        }
                        return (
                          <Fragment key={m.personId}>
                            <td className="av-bm-sign">{cell.sign || '—'}</td>
                            <td className="av-bm-dir">{cell.direction || '—'}</td>
                          </Fragment>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="av-bm-stack">
              {members.map((m, mi) => (
                <section className="av-bm-mcard" key={m.personId} aria-label={`Box matrix — ${m.name}`}>
                  <h3 className="av-bm-mname">
                    {m.name}
                    {m.isBreadwinner && <span className="av-bm-crown" aria-hidden> ♔</span>}
                  </h3>
                  {m.boxMatrix.length > 0 ? (
                    <ul className="av-bm-mlist">
                      {boxScaffold.map((row) => {
                        const cell = memberBoxMaps[mi].get(row.box);
                        return (
                          <li key={row.box}>
                            <span className="av-bm-num">{row.box}</span>
                            <span className="av-bm-ctx">{row.lifeContext || '—'}</span>
                            <span className="av-bm-sign">{cell?.sign || '—'}</span>
                            <span className="av-bm-dir">{cell?.direction || '—'}</span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="av-bm-mpending">
                      {m.chartStatus === 'pending'
                        ? 'Chart pending — the 12 boxes fill in once the chart resolves.'
                        : 'No box data for this member.'}
                    </p>
                  )}
                </section>
              ))}
            </div>
            {pendingMembers.length > 0 && (
              <p style={{ fontSize: 11.5, color: 'var(--ink-soft)', margin: '8px 0 0', lineHeight: 1.5 }}>
                Columns showing <b>—</b> are members whose chart is still pending
                {' '}({pendingMembers.map((m) => m.name).join(', ')}); their signs fill in once the
                chart resolves.
              </p>
            )}
          </>
        ) : (
          <div className="av-empty">
            The Box Matrix fills in once at least one family member's chart resolves. Capture
            birth details and link the astro family, then the 12-box comparison appears here.
          </div>
        )}
      </Card>

      {/* 6 · ENTRANCE × CHART VERDICT — the chart-IDEAL entrance (chart-ideal source) */}
      {bwVerdict ? (
        <Card>
          <h2 style={{ fontSize: 14.5, fontWeight: 700, margin: '0 0 3px' }}>Entrance × chart verdict</h2>
          <p style={{ fontSize: 12, color: 'var(--ink-mute)', margin: '0 0 14px', lineHeight: 1.5 }}>
            The ideal entrance for the breadwinner's chart. <b>Honest scope:</b> this is the
            chart's <i>ideal</i> entrance — <b>not yet reconciled</b> against the home's marked
            entrance.
          </p>
          <div className="av-p2-grid">
            <div className="av-p2-cell">
              <div className="av-lab">Breadwinner chart</div>
              <div className="av-val">
                {breadwinner ? `${breadwinner.name} · ${bwAsc?.en ?? '—'}${bwAsc?.sa ? ` / ${bwAsc.sa}` : ''}` : '—'}
              </div>
            </div>
            <div className="av-p2-cell">
              <div className="av-lab">Ideal entrance for this chart</div>
              {bwVerdict.disposition === 'no-ideal' ? (
                <>
                  <div className="av-val">No ideal entrance on this chart</div>
                  {bwVerdict.noIdealReason && <div className="av-ent-reason">{bwVerdict.noIdealReason}</div>}
                </>
              ) : (
                <>
                  <div className="av-big-zone">{bwVerdict.recommendationZone ?? '—'}</div>
                  <div className="av-sub">
                    {bwVerdict.recommendationZone ? zoneName(bwVerdict.recommendationZone) : 'Awaiting chart'}
                    {bwVerdict.recommendationPada ? ` · pada ${bwVerdict.recommendationPada}` : ''}
                    {bwVerdict.recommendationBox ? ` · Box ${bwVerdict.recommendationBox}` : ''}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="av-ent-foot">
            <span className={`av-tag ${bwVerdict.disposition === 'no-ideal' ? 'warn' : 'good'}`}>
              <span aria-hidden>{bwVerdict.disposition === 'no-ideal' ? '≈ ' : '✓ '}</span>
              {bwVerdict.disposition === 'no-ideal' ? 'No ideal entrance available' : 'Ideal entrance available'}
            </span>
            {bwVerdict.source === 'chart-ideal' && (
              <span className="av-ent-honest">
                <span aria-hidden>ⓘ</span> Chart-ideal — the astro-side ideal, not yet reconciled
                against the home's marked entrance.
              </span>
            )}
          </div>
          {bwVerdict.note && <p className="av-ent-note">{bwVerdict.note}</p>}
          {((bwVerdict.acceptablePadas?.length ?? 0) > 0 || (bwVerdict.avoidPadas?.length ?? 0) > 0) && (
            <div className="av-ent-padas">
              {bwVerdict.acceptablePadas && bwVerdict.acceptablePadas.length > 0 && (
                <div className="av-ent-padrow">
                  <span className="av-lab">Acceptable padas</span>
                  {bwVerdict.acceptablePadas.map((p, i) => (
                    <span className="av-tag good" key={`a${i}`}>
                      {p.pada ?? p.zone}{p.zone && p.pada ? ` · ${p.zone}` : ''}
                    </span>
                  ))}
                </div>
              )}
              {bwVerdict.avoidPadas && bwVerdict.avoidPadas.length > 0 && (
                <div className="av-ent-padrow">
                  <span className="av-lab">Avoid padas</span>
                  {bwVerdict.avoidPadas.map((p, i) => (
                    <span className="av-tag crit" key={`v${i}`}>
                      {p.pada ?? p.zone}{p.zone && p.pada ? ` · ${p.zone}` : ''}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>
      ) : (
        <div className="av-phase2">
          <span className="av-phase2-tag"><span aria-hidden>✦</span> Entrance × chart</span>
          <h3>Entrance × chart verdict</h3>
          <p className="av-note">
            The chart-ideal entrance for the breadwinner.{' '}
            {breadwinner
              ? `${breadwinner.name}'s chart is still resolving`
              : 'No breadwinner resolved yet'}{' '}
            — the ideal entrance zone appears here once the chart is ready.
          </p>
          <div className="av-p2-foot">
            <span className="av-p2-pill"><span aria-hidden>⏳</span> Awaiting the breadwinner's chart</span>
          </div>
        </div>
      )}

      {/* 7 · CONFIRM CARD — the owner's tick (ruling 2026-07-03). Confirming
          snapshots exactly what this screen shows into the project doc; the
          confirmed state swaps the button for the banner + a quiet Re-confirm. */}
      <div className="av-confirmbar">
        <div className="av-cb-txt">
          <b>Confirm astro inputs</b>
          <p>
            Locks the ascendants, ideal-vs-available zones (matched to your marked bedrooms),
            and the who-sleeps-where allocation. Feeds the report's{' '}
            <span className="av-feeds">§E ideal-vs-achievable</span> and{' '}
            <span className="av-feeds">§F who-sleeps-where</span>. The entrance verdict is
            snapshotted as the chart-ideal — not yet reconciled against the home's marked
            entrance.
          </p>
          {confirmation && (
            <p className="av-cb-done">
              <span className="av-status-pill good" role="status">
                <span aria-hidden>✓ </span>
                Confirmed by {confirmation.confirmed_by_name} on{' '}
                {confirmedOn(confirmation.confirmed_at)}
              </span>
            </p>
          )}
        </div>
        <div className="av-cb-actions">
          {confirmation ? (
            <Btn3
              intent="ghost"
              size="sm"
              disabled={confirmBusy || !mayCommit}
              title={
                !mayCommit
                  ? 'Only the project assignee or an admin can re-confirm'
                  : 'Overwrite the confirmation with a fresh snapshot of what this screen shows now'
              }
              onClick={confirmAstroInputs}
            >
              {confirmBusy ? 'Re-confirming…' : 'Re-confirm'}
            </Btn3>
          ) : (
            <Btn3
              intent="grn"
              size="sm"
              disabled={confirmBusy || !mayCommit}
              title={
                !mayCommit
                  ? 'Only the project assignee or an admin can confirm'
                  : 'Lock these astro inputs into the report'
              }
              onClick={confirmAstroInputs}
            >
              {confirmBusy ? 'Confirming…' : 'Confirm astro inputs'}
            </Btn3>
          )}
        </div>
      </div>
    </div>
  );
}
